import { Injectable } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import {
  createDynamicEventName,
  fromDynamicEvent,
} from '@epicuro-next/platform/dynamic-actions';
import {
  ListEntity,
  selectOpenedListEntity,
} from '@epicuro-next/state/list-entities';
import { getValue } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, shareReplay, takeUntil, tap } from 'rxjs/operators';

interface State {
  listEntity: ListEntity | null;
  isPowerListPreviewMode: boolean;
}

@Injectable()
export class OpenedListEntityProviderService
  extends ComponentStore<State>
  implements AbstractDataProvider
{
  private listEntity$ = this.select((state) => state.listEntity);
  private bindings = new Map<string, Observable<unknown>>();
  private powerListPreviewMode$ = this.select(
    (state) => state.isPowerListPreviewMode,
  );

  public togglePreviewMode$ = this.effect<void>((event$: Observable<void>) => {
    return event$.pipe(
      tap(() => {
        this.patchState((state) => ({
          isPowerListPreviewMode: !state.isPowerListPreviewMode,
        }));
      }),
    );
  })(fromDynamicEvent(createDynamicEventName('toggle-preview-mode')));

  constructor(private store: Store) {
    super({
      listEntity: null,
      isPowerListPreviewMode: false,
    });

    this.subscribeListEntity();
    this.initBindings();
  }

  public read<T>(property: string): Observable<T> {
    let observable = this.bindings.get(property);

    if (observable) {
      return observable as Observable<T>;
    }

    observable = this.listEntity$.pipe(
      map((entity) => {
        if (!entity) {
          return null;
        }

        return getValue(entity, property);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );

    this.bindings.set(property, observable);

    return observable as Observable<T>;
  }

  public write() {
    // for linter
  }

  private subscribeListEntity() {
    this.store
      .pipe(select(selectOpenedListEntity), takeUntil(this.destroy$))
      .subscribe((listEntity) => {
        this.patchState({ listEntity, isPowerListPreviewMode: false });
      });
  }

  private initBindings() {
    const bindingsEntries: [string, Observable<unknown>][] = [
      ['powerListPreviewMode', this.powerListPreviewMode$],
    ];

    bindingsEntries.forEach(([propertyName, observer]) => {
      this.initBinding(propertyName, observer);
    });
  }

  private initBinding(propertyName: string, observer: Observable<unknown>) {
    this.bindings.set(
      propertyName,
      observer.pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.destroy$),
      ),
    );
  }
}
